// Dummy data — UI prototype, nothing is wired up.
//
// NOTE: the real backend sampling enum is only `Random | Other` today. This richer method set is
// forward-looking prototype vocabulary for the methodology UI.
import {FieldType} from "./populationsData";

export type SampleMethod =
  | "full"
  | "random"
  | "systematic"
  | "monetary_unit"
  | "haphazard"
  | "judgemental"
  | "stratified"
  | "time_frequency";

/** How often a recurring control operates — drives the time-frequency sample size. */
export type ControlFrequency = "daily" | "weekly" | "monthly" | "quarterly" | "annually";

// `attribute`, where set, is the population field the method keys off (e.g. the monetary value
// for MUS, or the field to stratify by); `attributeType`, where set, restricts which schema
// columns can be chosen (e.g. MUS can only weight by a currency field). Methods without an
// attribute select across the whole population. `usesRandomness` flags methods that draw
// randomly under the hood — these need a seed so the selection is reproducible. `comingSoon`
// methods are shown in the picker but not yet selectable — only Random is implemented for now.
export const SAMPLE_METHOD_INFO: Record<
  SampleMethod,
  {
    label: string;
    description: string;
    attribute?: string;
    attributeType?: FieldType;
    usesRandomness?: boolean;
    comingSoon?: boolean;
  }
> = {
  full: {label: "Full population", description: "Test every item — 100% coverage, no sampling."},
  random: {label: "Random", description: "Every item has an equal chance of selection.", usesRandomness: true},
  time_frequency: {
    label: "Time-frequency",
    description: "Spread selections across a date field, sized by operating frequency.",
    attribute: "Date field",
    attributeType: "date",
    usesRandomness: true,
  },
  systematic: {
    label: "Systematic",
    description: "Select every nth item from a random start.",
    usesRandomness: true,
    comingSoon: true,
  },
  monetary_unit: {
    label: "Monetary-unit (MUS)",
    description: "Selection probability proportional to value.",
    attribute: "Value attribute",
    attributeType: "currency",
    usesRandomness: true,
    comingSoon: true,
  },
  haphazard: {label: "Haphazard", description: "Non-statistical selection with no conscious bias.", comingSoon: true},
  judgemental: {label: "Judgemental", description: "Targeted selection against defined criteria."},
  stratified: {
    label: "Stratified",
    description: "Split the population into strata and sample each.",
    attribute: "Stratify by",
    usesRandomness: true,
    comingSoon: true,
  },
};

/** A test that consumes a sample (for the "Used in" tab). */
export type SampleTestRef = {id: string; name: string; status: string};

/**
 * Method-specific configuration shown on the Sample design tab. Only the fields relevant to the
 * sample's `method` are populated (random → size basis + size + seed; judgemental → criteria).
 */
export type SampleConfig = {
  /** Random: whether the size is a percentage of the population or a fixed count. */
  sizeBasis?: "percentage" | "fixed";
  /** Random: the configured size — a percentage like "10%" or a fixed count like "25". */
  sampleSize?: string;
  /** Methods that draw randomly: the seed that makes the draw reproducible. */
  seed?: string;
  /** Judgemental: the natural-language criteria the selection targets. */
  criteria?: string;
  /** Time-frequency: the date field the selected occurrences are spread across. */
  dateField?: string;
  /** Time-frequency: how often the control operates over the period. */
  frequency?: ControlFrequency;
};

export type SampleRow = {
  /** URL slug used to route to the sample detail page. */
  id: string;
  name: string;
  description: string;
  /** The population this sample is drawn from. */
  populationId: string;
  populationName: string;
  populationSize: string;
  method: SampleMethod;
  /** Number of items selected (display count). */
  size: string;
  /** Sample size as a share of the population. */
  coverage: string;
  usedIn: string;
  lastRun: string;
  /** When locked, the sample is frozen — its selection and methodology can't be edited. */
  locked?: boolean;
  /** Method-specific configuration for the Sample design tab. */
  config: SampleConfig;
  tests: SampleTestRef[];
  /**
   * Shared supporting evidence for the drawn items, keyed by population-row index — itemEvidence[i]
   * are the files attached to the item at population.rows[i]. Inherited (read-only) by every test
   * that uses this sample, so the same documents don't have to be re-gathered per test.
   */
  itemEvidence?: string[][];
  /** Uploaded sample evidence not yet matched to a specific item. */
  unmatchedEvidence?: string[];
};

export const SAMPLES: SampleRow[] = [
  {
    id: "gl-high-value",
    name: "High-value journal entries",
    description: "Targeted selection of high-value journal entries above the de minimis threshold.",
    populationId: "gl-journal-entries",
    populationName: "GL journal entries",
    populationSize: "12,480",
    method: "judgemental",
    size: "45",
    coverage: "0.4%",
    usedIn: "1 test",
    lastRun: "2 days ago",
    locked: true,
    config: {
      criteria:
        "Journal entries with an absolute value at or above the £50,000 de minimis threshold, " +
        "together with any manual entries posted outside normal business hours.",
    },
    tests: [{id: "toc-je-authorisation", name: "Journal entry authorisation", status: "Complete"}],
    itemEvidence: [
      ["GL extract Q1.pdf", "Authorisation matrix.xlsx"],
      ["GL extract Q1.pdf"],
      ["GL extract Q1.pdf", "Board minutes Mar.pdf"],
      [],
      ["GL extract Q1.pdf"],
    ],
  },
  {
    id: "vendor-random-25",
    name: "Active and inactive suppliers",
    description: "Random selection of active and inactive suppliers for approval testing.",
    populationId: "vendor-master-list",
    populationName: "Vendor master list",
    populationSize: "1,204",
    method: "random",
    size: "25",
    coverage: "2.1%",
    usedIn: "No tests",
    lastRun: "5 hours ago",
    config: {sizeBasis: "fixed", sampleSize: "25", seed: "20240419"},
    tests: [],
    itemEvidence: [["Supplier register.xlsx"], ["Supplier register.xlsx"], []],
  },
  {
    id: "po-random",
    name: "Authorised purchase orders",
    description: "Random selection of authorised purchase orders for controls testing.",
    populationId: "purchase-orders",
    populationName: "Purchase orders",
    populationSize: "8,902",
    method: "random",
    size: "45",
    coverage: "0.5%",
    usedIn: "1 test",
    lastRun: "Yesterday",
    config: {sizeBasis: "fixed", sampleSize: "45", seed: "20240705"},
    tests: [{id: "toc-po-3way-match", name: "Three-way match", status: "Complete"}],
    itemEvidence: [["PO approval log.pdf"], [], ["PO approval log.pdf", "GRN-5521.pdf"]],
    unmatchedEvidence: ["Receiving report batch.pdf"],
  },
  {
    id: "access-privileged",
    name: "Privileged user access",
    description: "Targeted selection of users with elevated or admin access.",
    populationId: "user-access-finance-app",
    populationName: "User access — Finance app",
    populationSize: "542",
    method: "judgemental",
    size: "18",
    coverage: "3.3%",
    usedIn: "No tests",
    lastRun: "3 days ago",
    locked: true,
    config: {
      criteria:
        "Users with elevated or administrator privileges in the finance application, together with " +
        "any access granted outside the standard joiner approval process.",
    },
    tests: [],
  },
  {
    id: "joiners-random",
    name: "New joiners and leavers",
    description: "Random sample of joiners and leavers recorded in the quarter.",
    populationId: "new-joiners-leavers",
    populationName: "New joiners & leavers",
    populationSize: "318",
    method: "random",
    size: "15",
    coverage: "4.7%",
    usedIn: "No tests",
    lastRun: "1 week ago",
    config: {sizeBasis: "fixed", sampleSize: "15", seed: "20240228"},
    tests: [],
  },
];
